"use client";

import React from "react";
import { Button, CircularProgress } from "@mui/material";
import { GovButton } from "./GovButton";

interface AuthButtonProps {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  loading: boolean;
  label: string;
  isGovButton?: boolean;
}

/**
 * Componente de botão de autenticação que renderiza um GovButton ou um Button padrão.
 *
 * @param props.onClick - Função chamada quando o botão é clicado.
 * @param props.loading - Se true, desabilita o botão e exibe um indicador de carregamento.
 * @param props.label - Texto ou nó React exibido dentro do botão.
 * @param props.isGovButton - Quando true, renderiza o componente GovButton em vez do Button padrão. Default: false.
 *
 * @returns JSX.Element - Um botão configurado conforme as propriedades recebidas.
 *
 * @example
 * <AuthButton onClick={handleLogin} loading={false} label="Entrar" />
 *
 * @remarks
 * - Sempre define type="button" para evitar submissão involuntária de formulários.
 * - Mantém feedback visual consistente durante o carregamento.
 */
const AuthButton: React.FC<AuthButtonProps> = ({
  onClick,
  loading,
  label,
  isGovButton = false,
}) => {
  if (isGovButton) {
    return (
      <GovButton type="button" onClick={onClick} disabled={loading}>
        {loading ? <CircularProgress size={20} /> : label}
      </GovButton>
    );
  }

  return (
    <Button
      variant="outlined"
      fullWidth
      type="button" // <- garante que não submete
      onClick={onClick}
      disabled={loading}
      sx={{
        py: 1.5,
        fontSize: "1rem",
        fontWeight: 600,
        textTransform: "none",
        borderRadius: 2,
        mb: 3,
        bgcolor: "var(--color-primary)",
        color: "var(--color-background)",
        "&:hover": {
          bgcolor: "var(--color-primary-dark)",
          color: "var(--color-background)",
          transition: "background-color 0.3s ease",
        },
      }}
    >
      {loading ? <CircularProgress size={20} /> : label}
    </Button>
  );
};

export default AuthButton;
